(ns practitest.frontend.permutations.events
  (:require
   [re-frame.core              :as re-frame]
   [ajax.core                  :as ajax]
   [clojure.string             :as string]
   [clojure.math.combinatorics :as combo]
   [day8.re-frame.http-fx]
   [practitest.frontend.common-old-fe.logging    :as log]))

;; ===========================================================================
;; interceptors

(def common-interceptors
  [(when ^boolean js/goog.DEBUG re-frame/debug)
   re-frame/trim-v])

;; ===========================================================================
;; utils

(defn checkbox-field->answers [fields [id options]]
  (->> options
       (filter last) ;; we're only interested in selected checkboxes
       (map (fn [[option-id _]]
              {:id     (get-in fields [id :id])
               :type   (get-in fields [id :type])
               :val    (get-in fields [id :options option-id :name])
               :val_id option-id}))))

(defn text-field->answers [fields [id values]]
  (when-not (string/blank? values)
    (->> (string/split values #"\s*,\s*")
         (map (fn [value]
                {:id     (get-in fields [id :id])
                 :type   (get-in fields [id :type])
                 :val    value
                 :val_id value})))))

;; ===========================================================================
;; event handlers

(re-frame/reg-event-db
 :permutations/initialize
 common-interceptors
 (fn [db [options]]
   (log/info ":permutations/initialize" options db (js->clj js/window._pt :keywordize-keys true))
   (dissoc (merge options db {:screen :first}) :permutations-checked)))

(re-frame/reg-event-fx
 :permutations/get-data
 common-interceptors
 (fn [{:keys [db]} _]
   {:db         (assoc db :back-to-url (:sets-path db))
    :http-xhrio {:method          :get
                 :uri             (:fields-path db)
                 :timeout         3000
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:permutations/data-good]
                 :on-failure      [:permutations/data-bad]}}))

(re-frame/reg-event-fx
 :permutations/submit
 (fn [{:keys [db]} _]
   {:http-xhrio {:method          :post
                 :uri             (:update-uri db)
                 :params          {:permutation (map first (filter last (:permutations-checked db)))
                                   :set-ids     (:set-ids db)
                                   :filter_name (:filter_name db)
                                   :failed_tests_only_checkbox (:failed-tests-only-checkbox db)}
                 :timeout         5000
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:permutations/good-post-result]
                 :on-failure      [:permutations/bad-post-result]}}))

(re-frame/reg-event-db
 :permutations/good-post-result
 common-interceptors
 (fn [db [data]]
   (log/info :permutations-checked db data)
   (.assign js/location (:back-to-url db))
   db))

(re-frame/reg-event-db
 :permutations/back-to-test-sets
 common-interceptors
 (fn [db [data]]
   (.assign js/location (:back-to-url db))
   db))

(re-frame/reg-event-db
 :permutations/bad-post-result
 common-interceptors
 (fn [db [data]]
   (log/info :permutations-checked db data)
   db))

(re-frame/reg-event-db
 :permutations/data-good
 common-interceptors
 (fn [db [data]]
   (let [data (map #(assoc % :int-id (str (if (= "SystemField" (:type %)) "sf-" "cf-") (:id %))) data)]
     (log/info :permutations-data db data)
     (assoc db
            :permutations-data data
            :checkboxes (->> data
                             (filter #(= "list" (:format %)))
                             (map (fn [{:keys [int-id options]}]
                                    [int-id
                                     (->> options
                                          (map (fn [{:keys [id]}]
                                                 [id false]))
                                          (into {}))]))
                             (into {}))
            :text-fields (->> data
                              (filter #(= "text" (:format %)))
                              (map (fn [{:keys [int-id]}]
                                     [int-id nil]))
                              (into {}))))))

(re-frame/reg-event-db
 :permutations/data-bad
 common-interceptors
 (fn [db [error]]
   (log/info ":permutations/data-bad" error)
   db))

(re-frame/reg-event-db
 :permutations/set-checkbox-state
 common-interceptors
 (fn [db [field-id option-id  value]]
   (log/info ":permutations/set-checkbox-state")
   (assoc-in db [:checkboxes field-id option-id] value)))

(re-frame/reg-event-db
 :permutations/set-failed-tests-only-checkbox-state
 common-interceptors
 (fn [db [value]]
   (assoc db :failed-tests-only-checkbox value)))

(re-frame/reg-event-db
 :permutations/set-all-checkbox-state
 common-interceptors
 (fn [db [field-id value]]
   (update-in db [:checkboxes field-id]
              (fn [options]
                (into {} (map (fn [[id _]] [id value]) options))))))

(re-frame/reg-event-db
 :permutations/set-text-fields-value
 common-interceptors
 (fn [db [field-id value]]
   (assoc-in db [:text-fields field-id] value)))

(re-frame/reg-event-db
 :permutations/set-optional-text-value
 common-interceptors
 (fn [db [value]]
   (assoc-in db [:filter_name] value)))

(re-frame/reg-event-fx
 :permutations/next-step
 common-interceptors
 (fn [{:keys [db]} _]
   ;; here we build the `answer` object similar to the old JS code
   ;; `fields` is a helper object to simplify lookups
   ;; we need it to lookup field type and option names
   ;; `answer` is a concatenation of two lists
   ;; first list is generated from all the selected checkboxes
   ;; second is generated from all comma-separated values of all text fields
   (let [fields (->> (:permutations-data db)
                     (map (fn [field]
                            [(:int-id field)
                             (update field :options (fn [options]
                                                      (into {} (map #(vector (:id %) %) (:options field)))))]))
                     (into {}))
         answer (concat
                 (mapcat (partial checkbox-field->answers fields) (:checkboxes db))
                 (mapcat (partial text-field->answers fields) (:text-fields db)))
         perms  (->> answer
                     (reduce #(update %1 (:id %2) conj %2) {})
                     vals
                     (apply combo/cartesian-product)
                     reverse)]
     (log/info :permutations/next-step (:permutations-data db) fields)
     (log/info :permutations/next-step answer)
     (log/info "permutate" perms)

     ;; i'm leaving the 'answer' object calculation here too,
     ;; just because thats how it was sent to the server in the Ziv's version
     ;; but the 'permutations' that are shown to the user
     ;; are actually cartesian product of sequences
     ;; where each sequence is the list of selected values of the fields

     ;; this handler is an 'fx' handler because in the Ziv's code he sent the answer object to the server
     ;; we dont do it yet

     ;; set permutations in app-db
     {:db (assoc db :screen :second :answers answer :permutations perms)})))

(re-frame/reg-event-db
 :permutations/prev-step
 common-interceptors
 (fn [db _]
   (assoc db :screen :first)))

(re-frame/reg-event-db
 :permutations/permutation-selected
 common-interceptors
 (fn [db [field-id value]]
   (log/info ":permutations/permutation-selected" field-id value)
   (log/info "counter" (count [:permutations/permutation-selected]))
   (assoc-in db [:permutations-checked field-id] value)))

(re-frame/reg-event-db
 :permutations/select-all-permutations
 common-interceptors
 (fn [db _]
   (assoc db :permutations-checked (into {} (map #(vector % true) (:permutations db))))))

(re-frame/reg-event-db
 :permutations/unselect-all-permutations
 common-interceptors
 (fn [db _]
   (dissoc db :permutations-checked)))
